import type {
  AccountingKpis,
  DailyStockKpi,
  DayOfWeek,
  HoursData,
  NetworkDashboardKpis,
  NetworkDashboardLiveStoreCa,
  NetworkDashboardStoreAgeFilter,
  TimeSpentData,
} from '@gozoki/api-types'
import { type UseQueryResult, keepPreviousData, useQuery } from '@tanstack/react-query'
import {
  type FetchNetworkDashboardKpisParams,
  fetchAccountingKpis,
  fetchAccountingWarnings,
  fetchAllStockKpis,
  fetchHoursData,
  fetchNetworkDashboardKpis,
  fetchNetworkDashboardLiveStoreCa,
  fetchTimeSpentData,
} from '../queries/kpi.queries'

export const useNetworkDashboardKpis = (
  storeIds?: number[],
  storeAgeFilter: NetworkDashboardStoreAgeFilter = 'default',
  period?: FetchNetworkDashboardKpisParams,
  enabled = true,
): UseQueryResult<NetworkDashboardKpis, unknown> =>
  useQuery({
    queryKey: [
      'get-network-dashboard-kpis',
      storeIds,
      storeAgeFilter,
      period?.ageStoreIds,
      period?.startDate,
      period?.endDate,
      period?.activeStartDate,
      period?.activeEndDate,
      period?.cohortUnit,
      period?.cohortValue,
    ],
    queryFn: () => fetchNetworkDashboardKpis(storeIds, storeAgeFilter, period),
    placeholderData: keepPreviousData,
    enabled,
  })

export const useNetworkDashboardLiveStoreCa = (
  storeIds?: number[],
  storeAgeFilter: NetworkDashboardStoreAgeFilter = 'default',
): UseQueryResult<NetworkDashboardLiveStoreCa, unknown> =>
  useQuery({
    queryKey: ['get-network-dashboard-live-store-ca', storeIds, storeAgeFilter],
    queryFn: () => fetchNetworkDashboardLiveStoreCa(storeIds, storeAgeFilter),
    // 60s, in MILLISECONDS (react-query unit). The live CA endpoint runs a carts
    // aggregation + a write on every call; do NOT lower this (300 = 300ms = ~3 req/s).
    refetchInterval: 60_000,
  })

export const useStockKpis = (): UseQueryResult<DailyStockKpi[], unknown> =>
  useQuery({
    queryKey: ['get-stock-kpi'],
    queryFn: () => fetchAllStockKpis(),
  })

export const useAccountingKpis = (date: string): UseQueryResult<AccountingKpis, unknown> =>
  useQuery({
    queryKey: ['get-accounting-kpis', date],
    queryFn: () => fetchAccountingKpis(date),
  })

export const useAccountingWarnings = (): UseQueryResult<
  { date: string; difference: number }[],
  unknown
> =>
  useQuery({
    queryKey: ['get-accounting-warnings'],
    queryFn: () => fetchAccountingWarnings(),
  })

export const useHoursData = (
  days: DayOfWeek[],
  storeIds: number[],
  plage: 'AM' | 'PM' | 'DAY',
  dateStart: Date,
  dateEnd: Date,
): UseQueryResult<HoursData[], unknown> => {
  const plageKey = plage || 'DAY'

  return useQuery({
    queryFn: () => fetchHoursData(days, storeIds, plage, dateStart, dateEnd),
    queryKey: ['get-hours-data', days, storeIds, plageKey, dateStart, dateEnd],
  })
}

export const useTimeSpentDatas = (
  days: DayOfWeek[],
  storeIds: number[],
  plage: 'AM' | 'PM' | 'DAY',
  dateStart: Date,
  dateEnd: Date,
): UseQueryResult<TimeSpentData[], unknown> => {
  const plageKey = plage || 'day'
  return useQuery({
    queryFn: () => fetchTimeSpentData(days, storeIds, plage, dateEnd, dateStart),
    queryKey: ['get-time-spent-data', days, storeIds, plageKey, dateStart, dateEnd],
  })
}
